import Link from 'next/link';
import {MouseEvent, useCallback} from 'react';
import {openWindow} from '../common/openWindow.function';
import {preventDefaultEvent} from '../common/shared/utils/preventDefaultEvent.function';
import {UrlLinkProps} from './UrlLinkProps.interface';

/**
 * Next Link는 href에 undefined를 넣을 수 없습니다.
 * undefined 일때 처리와 새창으로 여는 처리를 해주는 Link 컴포넌트 입니다.
 */

export function UrlLink({
  children,
  url,
  linkStyle,
  linkClassName,
  draggable,
  preventEvent,
  onClick,
  shouldOpenNewWindow,
  shouldOpenNewTab,
  windowTitle,
  legacyBehavior,
  ...props
}: UrlLinkProps) {
  const onClickNewWindowLink = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      if (!shouldOpenNewWindow || !url) {
        return;
      }
      openWindow({
        url,
        target: windowTitle || url,
        features: 'width=460, height=700',
      });
    },
    [shouldOpenNewWindow, url, windowTitle]
  );

  if (!url) {
    return (
      <a
        onClick={onClick}
        className={linkClassName}
        style={linkStyle}
        draggable={draggable}
        {...props}
      >
        {children}
      </a>
    );
  }

  if (shouldOpenNewWindow) {
    return (
      <a
        onClick={e => {
          onClick && onClick(e);
          !preventEvent && onClickNewWindowLink(e);
        }}
        className={linkClassName}
        style={linkStyle}
        draggable={draggable}
        {...props}
      >
        {children}
      </a>
    );
  }

  if (shouldOpenNewTab) {
    return (
      <a
        onClick={onClick}
        className={linkClassName}
        style={linkStyle}
        draggable={draggable}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={url as string}
      /* 기본이 prefetch:true 인데 뷰포트에 들어오는 순간 SSG 인 페이지를 모두 가져오는 옵션이라 최초 접근의 경우 너무 느림. prefetch=false 로 변경(그래도 마우스오버시 prefetch 한다고 함) */ prefetch={
        false
      }
      passHref
      onClick={
        preventEvent || onClick
          ? e => {
              preventEvent && preventDefaultEvent(e);
              onClick && onClick(e);
            }
          : undefined
      }
      className={linkClassName}
      style={linkStyle}
      draggable={draggable}
      legacyBehavior={legacyBehavior}
      {...props}
    >
      {children}
    </Link>
  );
}
